import React, { useMemo, useState } from 'react'
import { ButtonBase, CircularProgress, Stack } from '@mui/material'

import { CustomTooltip } from '../ui/tooltip'

import TermSuggestionsDialog from './TermSuggestionsDialog'

import { type verificationStatuses } from '@/utils/getVerificationStatus'
import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { usePhraseVerification } from '@/hooks/usePhraseVerification'
import { verificationStatusIds } from '@/utils/getVerificationStatus'
import useRepeatAudioWizard from '@/views/NewBuilder/components/RepeatAudioWizard/useRepeatAudioWizard'
import RepeatAudioWizard from '@/views/NewBuilder/components/RepeatAudioWizard/RepeatAudioWizard'
import generateTermSuggestions from '@/utils/generateTermSuggestions'

interface VerificationStatusProps {
  handleVerifyTerm: (statusId: keyof typeof verificationStatuses, isChecked: boolean) => void
  handleUpdateTargetText: (targetText: CardActivityWithId['target_text']) => void
  card: CardActivityWithId
}

const buttonSx = {
  padding: '2px 6px',
  alignItems: 'center',
  borderRadius: '6px',
  border: '1px solid #D0D5DD',
  background: '#FFF',
  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
  color: '#344054',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '18px',
}

function VerificationStatus({ card, handleVerifyTerm, handleUpdateTargetText }: VerificationStatusProps): JSX.Element {
  const [loadingTermSuggestions, setLoadingTermSuggestions] = useState(false)
  const [termData, setTermData] = useState(null)
  const [openSuggestionsDialog, setOpenSuggestionsDialog] = useState(false)
  const { wizardOpen, wizardMode, showWizard, handleWizardClose: handleWizardCloseRepeat } = useRepeatAudioWizard()
  const memoizedTargetText = useMemo(() => card?.target_text ?? '', [card?.target_text])
  const memoizedLanguage = useMemo(() => card?.language ?? 'en', [card?.language])

  const { status, handleCheckNow, loading } = usePhraseVerification({
    targetText: memoizedTargetText,
    language: memoizedLanguage,
    onStatusChange: (statusId, isChecked) => {
      if (statusId) {
        handleVerifyTerm(statusId as keyof typeof verificationStatuses, isChecked)
      }
    },
    checked: card?.checked,
    verificationStatus: card?.verificationStatus,
    onCheckNow: () => {
      showWizard('verify-term')
    },
  })

  const { id, chip, description } = status || {}
  const isVerifyAction = [verificationStatusIds.NOT_CHECKED, verificationStatusIds.WARNING].includes(id)
  const isActionableStatus =
    isVerifyAction || [verificationStatusIds.NOT_RECOMMENDED, verificationStatusIds.NOT_WORKING].includes(id)
  const plainDescription = description?.replace(/\+.*?\+/g, '')

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isVerifyAction) {
      handleCheckNow()
    } else {
      setOpenSuggestionsDialog(true)
      handleGenerateTermSuggestions()
    }
  }

  const handleGenerateTermSuggestions = async () => {
    try {
      setLoadingTermSuggestions(true)
      const result = await generateTermSuggestions(card?.target_text, card?.language || 'en')

      setTermData(JSON.parse(result))
    } catch (e) {
    } finally {
      setLoadingTermSuggestions(false)
    }
  }

  const actionLabel = isVerifyAction ? 'Verify' : 'Fix'

  const handleUpdateTerm = (targetText: string) => {
    handleUpdateTargetText(targetText)
    handleVerifyTerm(null, false)
  }

  return (
    <>
      <TermSuggestionsDialog
        explanation={termData?.explanation || null}
        loading={loadingTermSuggestions}
        open={openSuggestionsDialog}
        options={termData?.options || []}
        onClose={() => {
          setOpenSuggestionsDialog(false)
        }}
        onUpdate={handleUpdateTerm}
      />
      <RepeatAudioWizard
        contextData={card}
        mode={wizardMode}
        open={wizardOpen}
        onClose={handleWizardCloseRepeat}
        onComplete={data => {
          handleVerifyTerm(data.verificationStatus!, data.checked)
        }}
      />
      <Stack alignItems="center" direction="row" gap="4px" minWidth={138}>
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <CustomTooltip placement="bottom" title={plainDescription} variant="dark">
              <span>{chip}</span>
            </CustomTooltip>

            {isActionableStatus ? (
              <ButtonBase sx={buttonSx} onClick={handleClick}>
                {actionLabel}
              </ButtonBase>
            ) : null}
          </>
        )}
      </Stack>
    </>
  )
}

export default React.memo(VerificationStatus)
